import React, { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, LayoutDashboard, Menu } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { canAccessDashboard, filterMenus, isOnlineFeatureEnabled } from "@/lib/permissions";
import { MENUS } from "@/lib/menuKeys";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";
import { useOnlineOrderInbox } from "@/hooks/useOnlineOrderInbox";
import { cn } from "@/lib/utils";

function NavBadge({ count }) {
  if (!count) return null;
  return (
    <span className="ms-1 inline-flex min-w-[18px] h-[18px] items-center justify-center rounded-full bg-brand-orange px-1 text-[10px] font-bold text-white">
      {count > 99 ? "99+" : count}
    </span>
  );
}

function isMenuActive(pathname, items) {
  return items.some((item) => pathname === item.to || pathname.startsWith(`${item.to}/`));
}

function itemBadge(item, counts) {
  if (item.to === "/online/orders") {
    return (counts.new || 0) + (counts.pending_delivery || 0);
  }
  return 0;
}

function itemPath(item, counts) {
  if (item.to === "/online/orders" && (counts.new || 0) + (counts.pending_delivery || 0) > 0) {
    return "/online/orders?attention=1";
  }
  return item.to;
}

function MobileNav({ visibleMenus, showDashboard, pathname, onNavigate, t, counts }) {
  return (
    <div className="flex flex-col gap-1 mt-2 px-2 pb-4">
      {showDashboard && (
        <Link
          to="/"
          onClick={onNavigate}
          className={cn(
            "flex items-center gap-2 px-3 py-2.5 rounded-lg text-[14px] font-medium mb-1 transition-colors",
            pathname === "/"
              ? "bg-brand-cream-panel text-brand-orange font-semibold"
              : "text-brand-brown hover:bg-brand-cream-panel",
          )}
        >
          <LayoutDashboard size={16} className="opacity-70" />
          {t("nav.dashboard")}
        </Link>
      )}
      {visibleMenus.map((menu) => {
        const isActive = isMenuActive(pathname, menu.items);
        return (
          <Collapsible
            key={menu.labelKey}
            defaultOpen={isActive}
            className="border-b border-brand-cream-border last:border-b-0"
          >
            <CollapsibleTrigger
              className={cn(
                "flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-[14px] font-medium transition-colors hover:bg-brand-cream-panel",
                isActive ? "text-brand-orange" : "text-brand-brown",
                "[&[data-state=open]>svg]:rotate-180",
              )}
            >
              <span>{t(menu.labelKey)}</span>
              <ChevronDown size={16} className="text-brand-brown/35 transition-transform duration-200" />
            </CollapsibleTrigger>
            <CollapsibleContent className="pb-1">
              <div className="flex flex-col pl-2">
                {menu.items.map((item) => {
                  const itemActive = pathname === item.to || pathname.startsWith(`${item.to}/`);
                  const badge = itemBadge(item, counts);
                  return (
                    <Link
                      key={item.to}
                      to={itemPath(item, counts)}
                      onClick={onNavigate}
                      className={cn(
                        "px-3 py-2 rounded-lg text-[13.5px] transition-colors inline-flex items-center justify-between",
                        itemActive
                          ? "bg-brand-cream-panel text-brand-orange font-medium"
                          : "text-brand-brown/80 hover:bg-brand-cream-panel hover:text-brand-brown",
                      )}
                    >
                      <span>{t(item.labelKey)}</span>
                      <NavBadge count={badge} />
                    </Link>
                  );
                })}
              </div>
            </CollapsibleContent>
          </Collapsible>
        );
      })}
    </div>
  );
}

const navItemBase =
  "px-2.5 h-8 inline-flex items-center gap-1.5 rounded-lg text-[12.5px] font-medium shrink-0 transition-colors";

export default function TopMenuBar() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const [mobileOpen, setMobileOpen] = useState(false);

  const visibleMenus = useMemo(
    () => filterMenus(MENUS, permissions, user),
    [permissions, user],
  );

  const showDashboard = canAccessDashboard(permissions);
  const { counts } = useOnlineOrderInbox({
    enabled: isOnlineFeatureEnabled(user) && permissions.includes("online.orders.view"),
  });

  if (!showDashboard && visibleMenus.length === 0) {
    return null;
  }

  return (
    <nav
      data-testid="top-menu-bar"
      className="h-10 flex items-center px-2 sm:px-3 bg-white text-brand-brown text-[12.5px] border-b border-[#E8D5C8] shadow-[0_1px_3px_rgba(43,11,4,0.06)] shrink-0 gap-0.5 min-w-0 z-40 relative overflow-visible"
    >
      <div className="flex items-center gap-2 pr-2 sm:pr-3 mr-1 border-r border-brand-cream-border shrink-0">
        <div className="h-7 w-7 rounded-lg bg-brand-orange flex items-center justify-center text-white font-bold text-[11px] shadow-sm shadow-orange-200/80">
          AM
        </div>
        <span className="hidden sm:inline font-bold text-[12.5px] tracking-tight text-brand-brown">
          {t("app.title")}
        </span>
      </div>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetTrigger asChild>
          <button
            type="button"
            className="lg:hidden h-8 w-8 inline-flex items-center justify-center rounded-lg text-brand-brown hover:bg-brand-cream-panel"
            aria-label="Open menu"
            data-testid="mobile-menu-btn"
          >
            <Menu size={18} />
          </button>
        </SheetTrigger>
        <SheetContent side="left" className="w-[min(100vw-2rem,320px)] p-0 overflow-y-auto border-brand-cream-border">
          <SheetHeader className="px-4 py-3 border-b border-brand-cream-border text-left bg-brand-cream/60">
            <SheetTitle className="text-[15px] text-brand-brown flex items-center gap-2">
              <span className="h-7 w-7 rounded-lg bg-brand-orange inline-flex items-center justify-center text-white text-[11px] font-bold">
                AM
              </span>
              {t("app.title")}
            </SheetTitle>
          </SheetHeader>
          <div className="px-2 pb-6">
            <MobileNav
              visibleMenus={visibleMenus}
              showDashboard={showDashboard}
              pathname={pathname}
              onNavigate={() => setMobileOpen(false)}
              t={t}
              counts={counts}
            />
          </div>
        </SheetContent>
      </Sheet>

      <div className="hidden lg:flex items-center gap-0.5 min-w-0 flex-1 overflow-visible">
        {showDashboard && (
          <Link
            to="/"
            className={cn(
              navItemBase,
              "hover:bg-brand-cream-panel",
              pathname === "/"
                ? "bg-brand-cream-panel text-brand-orange"
                : "text-brand-brown/75",
            )}
          >
            <LayoutDashboard size={13} />
            {t("nav.dashboard")}
          </Link>
        )}

        {visibleMenus.map((menu) => {
          const active = isMenuActive(pathname, menu.items);
          const menuBadge = menu.items.reduce((sum, item) => sum + itemBadge(item, counts), 0);
          return (
            <DropdownMenu key={menu.labelKey} modal={false}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    navItemBase,
                    "hover:bg-brand-cream-panel outline-none",
                    active ? "bg-brand-cream-panel text-brand-orange" : "text-brand-brown/75",
                  )}
                >
                  {t(menu.labelKey)}
                  <NavBadge count={menuBadge} />
                  <ChevronDown size={12} className="opacity-55" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                side="bottom"
                sideOffset={6}
                alignOffset={0}
                collisionPadding={12}
                className="z-[200] min-w-[220px] border-brand-cream-border bg-white p-1 shadow-lg"
              >
                {menu.items.map((item) => (
                  <DropdownMenuItem key={item.to} asChild>
                    <Link
                      to={itemPath(item, counts)}
                      className={cn(
                        "flex w-full cursor-pointer items-center justify-between rounded-md px-2 py-2 text-[13px]",
                        pathname === item.to && "bg-brand-cream-panel text-brand-orange font-medium",
                      )}
                    >
                      <span>{t(item.labelKey)}</span>
                      <NavBadge count={itemBadge(item, counts)} />
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        })}
      </div>

      <div className="ms-auto pe-1 shrink-0">
        <LanguageSwitcher
          className="border-brand-cream-border bg-brand-cream/80 text-brand-brown hover:bg-brand-cream-panel"
          compact
        />
      </div>

      {!showDashboard && visibleMenus.length === 1 && visibleMenus[0].items.length === 1 && (
        <Link
          to={visibleMenus[0].items[0].to}
          className={cn(navItemBase, "ml-1 hover:bg-brand-cream-panel text-brand-brown/80 truncate")}
        >
          {t(visibleMenus[0].items[0].labelKey)}
        </Link>
      )}
    </nav>
  );
}
